/**
 * Dial component.
 */
import AnalyticsInstance from '@shared/utils/analytics';
import Channel from '@shared/classes/channel';
import Input from '@components/atoms/text/dialInput';
import Pages from '@shared/types/enums/pages';
import goToZapperAction from '@shared/actions/channels/goToZapper';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { adsSelector } from '@shared/store/slices/ads.slice';
import { channelsSelector } from '@shared/store/slices/channels.slice';
import { clearModal, modalSelector } from '@shared/store/slices/modal.slice';
import { focusSelector } from '@shared/store/slices/focus.slice';
import { routerSelector } from '@shared/store/slices/router.slice';
import { setChannel } from '@shared/store/thunks/channels.thunk';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useRef, useState } from 'react';
import { OmnitrackPlaybackInteraction } from '@tvup/omnitrack-js';
import { DIAL_PAGES_BLACKLIST } from '@shared/types/constants/pageVisibility';
import { DIAL_FOCUS_BLACKLIST } from '@shared/types/constants/focusVisibility';
import { DURATION } from '@shared/types/constants/animations';

/**
 * Constants.
 *
 * @constant {string} DIAL_CHARACTER - The character to use for the dial input.
 * @constant {number} DIAL_DELAY - The delay in milliseconds for the dial input.
 * @constant {number} DIAL_LENGTH - The length of the dial input.
 */
const DIAL_LENGTH = 4;
const DIAL_TRIGGER_DELAY = 1.5 * 1000;

/**
 * Displays the dial input.
 */
export default function Dial() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { pauseAd } = useAppSelector(adsSelector);
  const { channelsData } = useAppSelector(channelsSelector);
  const { focus } = useAppSelector(focusSelector);
  const { current: currentModal } = useAppSelector(modalSelector);
  const { route } = useAppSelector(routerSelector);

  // States
  const [dial, setDial] = useState('');
  const [display, setDisplay] = useState(false);

  // Refs
  const canEdit = useRef(false);
  const pageRef = useRef(route.Page);
  canEdit.current =
    !DIAL_PAGES_BLACKLIST.includes(route.Page) &&
    !DIAL_FOCUS_BLACKLIST.includes(focus as number) &&
    !currentModal &&
    !pauseAd;
  pageRef.current = route.Page;

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    const timeout = setTimeout(() => {
      const parsedDial = parseInt(dial);
      const foundDial = channelsData?.findIndex(
        (value: Channel) => value.dial === parsedDial
      );
      if (foundDial !== undefined && foundDial > -1) {
        AnalyticsInstance.setPlaybackInteraction(
          OmnitrackPlaybackInteraction.REMOTE_SHORTCUT
        );
        dispatch(setChannel(parsedDial));
        dispatch(clearModal());
        if (pageRef.current !== Pages.ZAPPER) {
          goToZapperAction();
        }
      }
      setDisplay(false);
      setTimeout(() => {
        setDial('');
      }, DURATION.DIAL_INPUT);
    }, DIAL_TRIGGER_DELAY);
    return () => {
      clearTimeout(timeout);
    };
  }, [channelsData, dial]);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  useKeyboard({
    canBeFocused: false,
    keyDownHandler: (key) => {
      if (!canEdit.current) return;
      let number = '';
      switch (key) {
        case '0':
        case 'ZERO':
          number = '0';
          break;
        case '1':
        case 'ONE':
          number = '1';
          break;
        case 'TWO':
          number = '2';
          break;
        case 'THREE':
          number = '3';
          break;
        case 'FOUR':
          number = '4';
          break;
        case 'FIVE':
          number = '5';
          break;
        case 'SIX':
          number = '6';
          break;
        case 'SEVEN':
          number = '7';
          break;
        case 'EIGHT':
          number = '8';
          break;
        case 'NINE':
          number = '9';
          break;
      }
      if (number) {
        setDial((dial) => {
          return dial.length < DIAL_LENGTH ? dial + number : dial;
        });
        setDisplay(true);
      }
    },
  });

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <Input display={display} duration={DURATION.DIAL_INPUT}>
      {dial}
    </Input>
  );
}
